import { ApolloClient, InMemoryCache, HttpLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiOutage, monitoredFetch, type OutageMonitor } from './outage';

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
    });
  }
  
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
  }
});

// Auth link for adding authentication headers
const authLink = setContext(async (_, { headers }) => {
  try {
    // Get token from AWS Amplify
    const authSession = await fetchAuthSession();
    const token = authSession.tokens?.idToken?.toString();
    
    console.log('Auth token available:', !!token);
    
    // Return headers with auth token if available
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      }
    };
  } catch (error) {
    console.error('Error getting auth token:', error);
    // Return headers without auth token
    return {
      headers
    };
  }
});

// Create cache with type policies for proper merging
const createCache = () => new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        meetings: {
          merge(_existing = [], incoming) {
            return [...incoming];
          }
        },
        races: {
          merge(_existing = [], incoming) {
            return [...incoming];
          }
        },
        runners: {
          merge(_existing = [], incoming) {
            return [...incoming];
          }
        },
        results: {
          merge(_existing = [], incoming) {
            return [...incoming];
          }
        }
      }
    }
  }
});

// Each brand passes its own API URL; everything else is identical across brands.
// Every request's outcome feeds the outage monitor behind the maintenance screen.
export const createApolloClient = (uri: string, outage: OutageMonitor = apiOutage) => new ApolloClient({
  link: from([errorLink, authLink, new HttpLink({ uri, credentials: 'include', fetchOptions: { mode: 'cors' }, fetch: monitoredFetch(outage) })]),
  cache: createCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
  },
  connectToDevTools: import.meta.env.MODE === 'development',
});
